/**
 * ADR-0102 / ADR-0192 — File Provider inspector.
 *
 *   POST /api/tenant/operations/:id/nodes/:nodeId/file-provider/sniff
 *   POST /api/tenant/operations/:id/nodes/:nodeId/file-provider/upload
 *   GET  /api/tenant/operations/:id/nodes/:nodeId/file-provider/uploads
 *
 * Reads/writes `spec_file_provider` on the node config (parser, parser_config,
 * retention_policy, snapshot_max_versions?, column_mappings, current_upload_id?)
 * through the editor's autosave, and shows the step's DECLARED schema
 * (ADR-0192 §4.1: the current upload's column names + the step's
 * `column_type_overrides`) in the Data slot with the shared Domain control —
 * the same gesture, toast and revert the provider Schema Contract uses
 * (`changeNodeColumnDomain` in OperationEditor). When the step is schema-
 * locked the control is disabled: the lock is the one switch (owner ruling
 * 2026-09-09) — a locked declaration only changes by unlock → retype/upload.
 *
 * The wizard tells the truth: rows default from the sniff's real resolution
 * (spec §3 F2 closed); the Type column reads the declaration (or the seed on
 * a first upload, the only per-upload type choice that survives); a refusal
 * renders inline — a value that does not fit, or a locked step refusing a
 * changed column set — with the wizard kept open so the person can fix the
 * step or the file. Pure logic lives in `lib/fileProviderWizard.ts`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { Node } from "@xyflow/react";
import type { OperationCanvasNodeData } from "../OperationCanvasNode.js";
import { ApiRequestError } from "../../lib/api.js";
import { useToast } from "../Toast.js";
import { InspectorTabs } from "../inspector/InspectorTabs.js";
import { ColumnDomainSelect, type ColumnDomain } from "../ColumnDomainControl.js";
import { isColumnDomain, type TypeValidationFailures } from "../../lib/columnDomain.js";
import {
  NO_COLUMNS_YET,
  SCHEMA_LOCKED_UPLOAD_MESSAGE,
  UNLOCK_TO_RETYPE_HELP,
  columnsSummary,
  driftSentences,
  rowTypeCell,
  rowTypeText,
  rowsFromSniff,
  rowsToColumnResolutions,
  seedDomain,
  typeValidationInlineMessage,
  type WizardRow,
} from "../../lib/fileProviderWizard.js";
import {
  fileProviderSniff,
  fileProviderUpload,
  fileProviderUploadsList,
  getUploadLimits,
  type FileProviderUploadRow,
} from "../../lib/pdoUploadApi.js";
import {
  ANALYZING_LABEL,
  compressingLabel,
  exceedsUploadLimit,
  fileTooLargeMessage,
  uploadProgressLabel,
} from "../../lib/pdoUploadTypes.js";
import type {
  DownstreamKeyedPersist,
  SniffResult,
  StructuralDriftPreview,
  UploadParser,
  UploadParserConfig,
  UploadRetentionPolicy,
} from "../../lib/pdoUploadTypes.js";

const ACCEPT_EXTS = ".xlsx,.csv,.tsv,.json,.jsonl,.parquet";

const SNAPSHOT_RETENTION_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 5, label: "5" },
  { value: 10, label: "10" },
  { value: 20, label: "20" },
  { value: 50, label: "50" },
  { value: 100, label: "100" },
  { value: 1000, label: "Unlimited" },
];

function detectParser(filename: string): UploadParser | null {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".xlsx")) return "xlsx";
  if (lower.endsWith(".csv")) return "csv";
  if (lower.endsWith(".tsv")) return "tsv";
  if (lower.endsWith(".jsonl")) return "jsonl";
  if (lower.endsWith(".json")) return "json";
  if (lower.endsWith(".parquet")) return "parquet";
  return null;
}

function messageFromError(err: unknown): string {
  if (err instanceof ApiRequestError) {
    return err.message || err.code || "Request failed";
  }
  return err instanceof Error ? err.message : String(err);
}

/**
 * Absolute timestamp formatter — never use relative time anywhere in Atlas
 * UI. See `feedback_no_relative_timestamps.md` for the standing rule.
 */
function absoluteTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type SpecFileProvider = {
  parser?: UploadParser;
  parser_config?: UploadParserConfig;
  retention_policy?: UploadRetentionPolicy;
  snapshot_max_versions?: number;
  column_mappings?: Record<string, string>;
  current_upload_id?: string;
  name_override?: string;
};

type Refusal =
  | { kind: "drift"; drift: StructuralDriftPreview | null; downstream: DownstreamKeyedPersist[] }
  | { kind: "locked"; drift: StructuralDriftPreview | null }
  | { kind: "values"; message: string };

export function OperationInspectorFileProvider({
  selectedNode,
  operationId,
  setNodeData,
  rememberToken,
  nodeOutputSchema,
  columnOverrides,
  onColumnDomainChange,
  retypeBusy,
  schemaLocked,
}: {
  selectedNode: Node<OperationCanvasNodeData>;
  operationId: string;
  setNodeData: (nodeId: string, updater: (data: OperationCanvasNodeData) => OperationCanvasNodeData) => void;
  /**
   * ADR-0159 — adopt an operation token this component's own writes moved.
   * The upload writes the operation spec server-side, so the editor's
   * remembered token is dead the moment the 201 lands. See `handleSave`.
   */
  rememberToken: (updatedAt: string | null | undefined) => void;
  /** The node's cached output shape (`operation_node_outputs.output_schema`), if the editor has it. */
  nodeOutputSchema: Array<{ name: string; type: string }> | null;
  /** ADR-0147 / ADR-0192 — the step's declared types (dense on a file step). */
  columnOverrides: Record<string, string> | undefined;
  /** The editor's retype gesture: PATCH the map, POST /retype, toast + revert on a rejection. */
  onColumnDomainChange: (columnName: string, domain: ColumnDomain | null) => void;
  retypeBusy: boolean;
  /** ADR-0146 lock state, with the shell's pill title when locked. */
  schemaLocked: { locked: boolean; title: string | null };
}) {
  const toast = useToast();
  const cfg = (selectedNode.data.config?.spec_file_provider as SpecFileProvider | undefined) ?? {};

  // Retention policy + cap — locally editable, mirrored to spec_file_provider.
  const [retentionPolicy, setRetentionPolicy] = useState<UploadRetentionPolicy>(cfg.retention_policy ?? "replace");
  const [snapshotMax, setSnapshotMax] = useState<number>(cfg.snapshot_max_versions ?? 20);
  useEffect(() => {
    setRetentionPolicy(cfg.retention_policy ?? "replace");
    setSnapshotMax(cfg.snapshot_max_versions ?? 20);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode.id]);

  // Mirror spec_file_provider edits onto the node spec via autosave path.
  const patchSpecFileProvider = useCallback((patch: Partial<SpecFileProvider>) => {
    setNodeData(selectedNode.id, (d) => {
      const c = (d.config ?? {}) as Record<string, unknown>;
      const sp = ((c.spec_file_provider as Record<string, unknown>) ?? {}) as Record<string, unknown>;
      return { ...d, config: { ...c, spec_file_provider: { ...sp, ...patch } } };
    });
  }, [selectedNode.id, setNodeData]);

  // Uploads history.
  const [uploads, setUploads] = useState<FileProviderUploadRow[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const reloadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const data = await fileProviderUploadsList({ operationId, nodeId: selectedNode.id });
      setUploads(data.uploads);
    } catch (e) {
      setHistoryError(messageFromError(e));
    } finally {
      setHistoryLoading(false);
    }
  }, [operationId, selectedNode.id]);
  useEffect(() => { void reloadHistory(); }, [reloadHistory]);

  // A retype re-writes the current upload's recorded schema server-side;
  // re-read the history once the gesture settles so the Columns table and the
  // history's Columns cell show what the file now holds.
  const wasRetyping = useRef(false);
  useEffect(() => {
    if (wasRetyping.current && !retypeBusy) void reloadHistory();
    wasRetyping.current = retypeBusy;
  }, [retypeBusy, reloadHistory]);

  const currentUpload = uploads?.find((u) => u.id === cfg.current_upload_id) ?? uploads?.find((u) => !u.superseded_at) ?? null;

  // The declared column list: names + types of the current upload (reloaded
  // after every upload and retype), falling back to the editor's cached
  // output shape when the history has not loaded yet.
  const declaredColumns: Array<{ name: string; type: string }> =
    currentUpload?.schema?.columns?.map((c) => ({ name: c.name, type: c.type })) ??
    nodeOutputSchema ??
    [];

  // Wizard state.
  type Step = "view" | "reading" | "configure" | "uploading";
  const [step, setStep] = useState<Step>("view");
  const [file, setFile] = useState<File | null>(null);
  const [sniff, setSniff] = useState<SniffResult | null>(null);
  const [rows, setRows] = useState<WizardRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  /**
   * What the reading step says right now: compressing, then bytes on the wire,
   * then analyzing. A 95 MB CSV spends real seconds in each, and the 2026-09-12
   * defect was that all of them looked identical to nothing happening.
   */
  const [readingLabel, setReadingLabel] = useState<string>("");

  function resetWizard() {
    setStep("view");
    setFile(null);
    setSniff(null);
    setRows([]);
    setError(null);
    setRefusal(null);
    setReadingLabel("");
  }

  async function handlePick(f: File) {
    setError(null);
    setRefusal(null);
    const parser = detectParser(f.name);
    if (!parser) {
      setError("Unsupported file type. Allowed: .xlsx, .csv, .tsv, .json, .jsonl, .parquet");
      return;
    }
    // Refuse an over-cap file HERE, before a byte moves: the server can only say
    // no after the whole file has crossed the wire, and it says less.
    const limits = await getUploadLimits();
    if (limits && exceedsUploadLimit(f, limits)) {
      setError(fileTooLargeMessage(f, limits));
      return;
    }
    if (cfg.parser && cfg.parser !== parser) {
      toast.push({
        kind: "error",
        message: `This node was configured for .${cfg.parser} files; you uploaded .${parser}. The new parser will overwrite the previous setting.`,
      });
    }
    setFile(f);
    setBusy(true);
    setReadingLabel(compressingLabel(f));
    setStep("reading");
    try {
      const result = await fileProviderSniff({
        operationId,
        nodeId: selectedNode.id,
        file: f,
        onPhase: (phase) => {
          if (phase.kind === "compressing") setReadingLabel(compressingLabel(f));
          else if (phase.kind === "uploading") {
            setReadingLabel(uploadProgressLabel(phase.loadedBytes, phase.totalBytes));
          } else setReadingLabel(ANALYZING_LABEL);
        },
      });
      setSniff(result);
      setRows(rowsFromSniff(result));
      setStep("configure");
    } catch (e) {
      // Back to the view step, which renders `error` — a sniff that fails must
      // say so where the reader is looking, not in a step they never reach.
      setError(messageFromError(e));
      setFile(null);
      setStep("view");
    } finally {
      setBusy(false);
    }
  }

  function updateRow(idx: number, patch: Partial<WizardRow>) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  /** The target type per canonical column this upload would land — what a value failure is measured against. */
  function targetTypesByColumn(): Record<string, string | undefined> {
    const out: Record<string, string | undefined> = {};
    if (!sniff) return out;
    for (const r of rows) {
      if (r.user_resolution === "skip" || !r.canonical_name) continue;
      const cell = rowTypeCell(r, { isFirstUpload: sniff.is_first_upload, schemaLocked: sniff.schema_locked });
      out[r.canonical_name] = cell.kind === "change" ? cell.to : cell.domain;
    }
    return out;
  }

  async function handleSave(withConfirmDrift: boolean) {
    if (!sniff || !file) return;
    const unresolved = rows.filter(
      (r) => r.user_resolution !== "skip" && (!r.canonical_name || !r.canonical_name.trim()),
    );
    if (unresolved.length > 0) {
      setError(`${unresolved.length} column${unresolved.length === 1 ? "" : "s"} still need a canonical name`);
      return;
    }
    setError(null);
    setBusy(true);
    setStep("uploading");
    try {
      const result = await fileProviderUpload({
        operationId,
        nodeId: selectedNode.id,
        sniffId: sniff.sniff_id,
        originalFilename: file.name,
        parser: sniff.parser,
        parserConfig: sniff.applied_config,
        retentionPolicy,
        snapshotMaxVersions: retentionPolicy === "snapshot" ? snapshotMax : undefined,
        columnResolutions: rowsToColumnResolutions(rows, sniff.is_first_upload),
        confirmSchemaDrift: withConfirmDrift,
      });
      toast.push({
        kind: "success",
        message: `Uploaded ${file.name} · ${result.row_count.toLocaleString()} rows`,
      });
      // ADR-0159 — ORDER IS LOAD-BEARING. The upload moved the operation's
      // concurrency token server-side; `patchSpecFileProvider` below queues an
      // autosave. Adopting the new token FIRST is what stops that autosave
      // being refused against this tab's own gesture — the 2026-09-01 phantom
      // conflict banner. Never move this below the patch.
      rememberToken(result.operation_updated_at);
      // Mirror the server's spec_file_provider patch onto the local canvas
      // state so autosave doesn't stomp current_upload_id. The declaration
      // (`column_type_overrides`) is server-owned: the editor re-reads it with
      // the operation, and the applier keeps it when a save omits it.
      patchSpecFileProvider({
        parser: sniff.parser,
        parser_config: sniff.applied_config,
        retention_policy: retentionPolicy,
        ...(retentionPolicy === "snapshot" ? { snapshot_max_versions: snapshotMax } : {}),
        current_upload_id: result.upload_id,
      });
      resetWizard();
      await reloadHistory();
    } catch (e) {
      setStep("configure");
      if (e instanceof ApiRequestError) {
        if (e.code === "schema_drift_requires_confirmation") {
          const drift = (e.data?.drift as StructuralDriftPreview | undefined) ?? null;
          const downstream = (e.data?.downstream_keyed_persists as DownstreamKeyedPersist[] | undefined) ?? [];
          setRefusal({ kind: "drift", drift, downstream });
          setError(null);
          return;
        }
        if (e.code === "schema_locked") {
          const drift = (e.data?.drift as StructuralDriftPreview | undefined) ?? null;
          setRefusal({ kind: "locked", drift });
          setError(null);
          return;
        }
        if (e.code === "type_validation_failed") {
          const failures = (e.data?.failures ?? {}) as TypeValidationFailures;
          setRefusal({ kind: "values", message: typeValidationInlineMessage(failures, targetTypesByColumn(), sniff.schema_locked) });
          setError(null);
          return;
        }
      }
      setRefusal(null);
      setError(messageFromError(e));
    } finally {
      setBusy(false);
    }
  }

  const locked = schemaLocked.locked;

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <InspectorTabs
        surfaceId="operationEditor"
        tabGroupId="file_provider"
        defaultSlot="setup"
        tabs={[
          {
            slot: "info",
            content: (
              <div className="field">
                <label className="label">Node label</label>
                <input
                  className="input"
                  value={selectedNode.data.label}
                  onChange={(e) => setNodeData(selectedNode.id, (d) => ({ ...d, label: e.target.value }))}
                  placeholder="Unnamed"
                />
              </div>
            ),
          },
          {
            slot: "setup",
            content: (
    <div style={{ display: "grid", gap: 14 }}>

      {/* Retention policy */}
      <div className="card">
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Retention</div>
        <div className="field">
          <label className="label">Policy</label>
          <select
            className="select"
            value={retentionPolicy}
            onChange={(e) => {
              const next = e.target.value as UploadRetentionPolicy;
              setRetentionPolicy(next);
              patchSpecFileProvider(
                next === "snapshot"
                  ? { retention_policy: next, snapshot_max_versions: snapshotMax }
                  : { retention_policy: next, snapshot_max_versions: undefined },
              );
            }}
            disabled={busy}
          >
            <option value="replace">Replace — keep latest only</option>
            <option value="snapshot">Snapshot — keep history (capped)</option>
          </select>
        </div>
        {retentionPolicy === "snapshot" ? (
          <div className="field">
            <label className="label">Max versions</label>
            <select
              className="select"
              value={snapshotMax}
              onChange={(e) => {
                const n = Number(e.target.value);
                setSnapshotMax(n);
                patchSpecFileProvider({ retention_policy: "snapshot", snapshot_max_versions: n });
              }}
              disabled={busy}
            >
              {SNAPSHOT_RETENTION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {/* Current upload + uploader */}
      <div className="card">
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Current upload</div>
        {currentUpload ? (
          <div style={{ display: "grid", gap: 6, fontSize: 13 }}>
            <div>
              <span className="mono">{currentUpload.original_filename}</span>
              <span className="muted" style={{ marginLeft: 8 }}>
                · {currentUpload.row_count.toLocaleString()} rows
                {currentUpload.storage_bytes != null
                  ? ` · ${currentUpload.storage_bytes.toLocaleString()} bytes`
                  : ""}
              </span>
            </div>
            <div style={{ fontSize: 12 }}>
              <span className="muted">Uploaded:</span>{" "}
              <span>{absoluteTime(currentUpload.created_at)}</span>
            </div>
          </div>
        ) : (
          <div className="muted" style={{ fontSize: 13 }}>No uploads yet.</div>
        )}
        {/* A pick that never reached the wizard — wrong type, over the cap, a
            refused sniff — lands here, on the step the reader is looking at. */}
        {step === "view" && error ? (
          <div className="help" style={{ color: "var(--danger-text)", marginTop: 8 }}>{error}</div>
        ) : null}
        {step === "view" ? (
          <div className="actions" style={{ marginTop: 8 }}>
            <label className="btn btnSm btnPrimary" style={{ cursor: "pointer" }}>
              {currentUpload ? "Upload new version" : "Upload file"}
              <input
                type="file"
                accept={ACCEPT_EXTS}
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handlePick(f);
                  e.currentTarget.value = "";
                }}
              />
            </label>
          </div>
        ) : null}
      </div>

      {/* Wizard: configure step */}
      {step === "configure" && sniff ? (
        <div className="card">
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            Confirm columns from <span className="mono">{file?.name}</span>
          </div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
            {sniff.row_count.toLocaleString()} rows · parser <span className="mono">{sniff.parser}</span>
          </div>
          {error ? (
            <div className="help" style={{ color: "var(--danger-text)", marginBottom: 8 }}>{error}</div>
          ) : null}
          {refusal?.kind === "values" ? (
            <div className="help" style={{ color: "var(--danger-text)", marginBottom: 8 }}>{refusal.message}</div>
          ) : null}
          {refusal?.kind === "locked" ? (
            <div className="help" style={{ color: "var(--danger-text)", marginBottom: 8 }}>
              <div>{SCHEMA_LOCKED_UPLOAD_MESSAGE}</div>
              <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
                {driftSentences(refusal.drift, null).map((line) => <li key={line}>{line}</li>)}
              </ul>
            </div>
          ) : null}
          {refusal?.kind === "drift" ? (
            <div className="help" style={{ color: "var(--danger-text)", marginBottom: 8 }}>
              <div>This upload changes the step's schema:</div>
              <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
                {driftSentences(refusal.drift, refusal.downstream).map((line) => <li key={line}>{line}</li>)}
              </ul>
            </div>
          ) : null}
          <div className="tableWrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Source header</th>
                  <th>Canonical name</th>
                  <th>Type</th>
                  <th>Resolution</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const cell = rowTypeCell(r, { isFirstUpload: sniff.is_first_upload, schemaLocked: sniff.schema_locked });
                  return (
                  <tr key={r.source_header}>
                    <td><span className="mono">{r.source_header}</span></td>
                    <td>
                      <input
                        className="input"
                        type="text"
                        value={r.canonical_name ?? ""}
                        onChange={(e) => updateRow(i, { canonical_name: e.target.value })}
                        disabled={r.user_resolution === "skip" || busy}
                      />
                    </td>
                    <td>
                      {cell.kind === "seed" ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {/* The first upload's seed — the one per-upload type choice that survives (spec §5.1). */}
                          <ColumnDomainSelect
                            columnName={r.canonical_name ?? r.normalized_header}
                            resolvedDomain={seedDomain(r.inferred_type)}
                            override={r.type_override ?? undefined}
                            disabled={r.user_resolution === "skip" || busy}
                            onChange={(_col, domain) => updateRow(i, { type_override: isColumnDomain(domain) ? domain : null })}
                          />
                          <span className="muted" style={{ fontSize: 12 }}>from file</span>
                        </span>
                      ) : (
                        <span>{rowTypeText(cell)}</span>
                      )}
                    </td>
                    <td>
                      <select
                        className="select"
                        value={r.user_resolution}
                        onChange={(e) => updateRow(i, { user_resolution: e.target.value as WizardRow["user_resolution"] })}
                        disabled={busy}
                      >
                        <option value="existing">Map</option>
                        <option value="new">New</option>
                        <option value="skip">Skip</option>
                      </select>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="actions" style={{ marginTop: 12 }}>
            {refusal?.kind === "locked" ? null : (
              <button
                className="btn btnSm btnPrimary"
                type="button"
                onClick={() => void handleSave(refusal?.kind === "drift")}
                disabled={busy}
              >
                {busy ? "Uploading…" : refusal?.kind === "drift" ? "Confirm + upload" : "Upload"}
              </button>
            )}
            <button className="btn btnSm" type="button" onClick={resetWizard} disabled={busy}>Cancel</button>
          </div>
        </div>
      ) : null}

      {step === "reading" && file ? (
        <div className="card">
          <div className="muted" style={{ fontSize: 13 }}>{readingLabel}</div>
        </div>
      ) : null}

      {step === "uploading" ? (
        <div className="card">
          <div className="muted" style={{ fontSize: 13 }}>Uploading {file?.name}…</div>
        </div>
      ) : null}
    </div>
            ),
          },
          {
            // ADR-0192 §5.2 (Option A) — the step's declared schema, with the
            // shared Domain control: the same cell the provider Schema
            // Contract uses, so the gesture, the toast on rejection and the
            // revert are the ones people already know. Owner ruling
            // 2026-09-09: the slot reads "Schema" — it holds the declaration,
            // not rows — via the rack's per-tab label override.
            slot: "data",
            label: "Schema",
            content: (
      <div className="card">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={{ fontWeight: 600 }}>Columns</span>
          {locked ? (
            <span className="pill" title={schemaLocked.title ?? undefined}>Schema locked</span>
          ) : null}
        </div>
        {declaredColumns.length === 0 ? (
          <div className="muted" style={{ fontSize: 13 }}>{NO_COLUMNS_YET}</div>
        ) : (
          <>
            <table className="table tableCompact">
              <thead>
                <tr>
                  <th>Column</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {declaredColumns.map((column) => (
                  <tr key={column.name}>
                    <td>{column.name}</td>
                    <td>
                      <ColumnDomainSelect
                        columnName={column.name}
                        resolvedDomain={column.type}
                        override={columnOverrides?.[column.name] ?? column.type}
                        disabled={locked || retypeBusy}
                        onChange={onColumnDomainChange}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {locked ? (
              <div className="help" style={{ marginTop: 6 }}>{UNLOCK_TO_RETYPE_HELP}</div>
            ) : null}
          </>
        )}
      </div>
            ),
          },
          {
            slot: "runs",
            content: (
      <details>
        <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 600, color: "var(--muted)", userSelect: "none" }}>
          Upload history
        </summary>
        <div style={{ marginTop: 8 }}>
          {historyLoading ? (
            <div className="muted" style={{ fontSize: 12 }}>Loading…</div>
          ) : historyError ? (
            <div className="help" style={{ color: "var(--danger-text)" }}>{historyError}</div>
          ) : !uploads || uploads.length === 0 ? (
            <div className="muted" style={{ fontSize: 12 }}>No uploads yet.</div>
          ) : (
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Filename</th>
                    <th>Rows</th>
                    <th>Uploaded</th>
                    <th>Status</th>
                    <th>Columns</th>
                  </tr>
                </thead>
                <tbody>
                  {uploads.map((u) => {
                    const cols = columnsSummary(u.schema);
                    return (
                    <tr key={u.id}>
                      <td><span className="mono">{u.original_filename}</span></td>
                      <td>{u.row_count.toLocaleString()}</td>
                      <td>{absoluteTime(u.created_at)}</td>
                      <td>
                        {u.superseded_at ? (
                          <span className="pill muted" title={`Superseded at ${absoluteTime(u.superseded_at)}`}>
                            superseded
                          </span>
                        ) : (
                          <span className="pill pillOk">current</span>
                        )}
                      </td>
                      <td><span title={cols.title}>{cols.text}</span></td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </details>
            ),
          },
        ]}
      />
    </div>
  );
}
